import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, USER_ID } from "../experience/api";
import "./table.css";
import { DISH_PHOTOS, normName } from "./dishPhotos";
import { Landing, SignIn, Menu, Cart, Sent, Rewards, Leaderboard, Rules, Pay, Thanks, TableNotFound } from "./screens";
import BuildMeal from "./BuildMeal";

const SCREENS = { landing: Landing, signin: SignIn, menu: Menu, cart: Cart, sent: Sent, rewards: Rewards, leaderboard: Leaderboard, rules: Rules, pay: Pay, thanks: Thanks, build: BuildMeal };

export default function TableApp({ noTable = false }) {
  const { table: tableParam, screen: screenParam } = useParams();
  const navigate = useNavigate();
  // noTable = public menu at "/" — no table preselected, the guest types it at checkout
  const table = noTable ? "" : (tableParam || localStorage.getItem("up_table") || "").slice(0, 12);
  const screen = SCREENS[screenParam] ? screenParam : "menu";  // QR scan lands STRAIGHT on the menu
  const [phone, setPhoneState] = useState(() => localStorage.getItem("up_phone") || "");
  const [me, setMe] = useState(null);
  const [dishes, setDishes] = useState(null);
  const [catOrder, setCatOrder] = useState([]);
  const [varGroups, setVarGroups] = useState([]);
  const [cart, setCartState] = useState(() => {
    try { return JSON.parse(localStorage.getItem("up_cart") || "{}"); } catch { return {}; }
  });
  const [tableOk, setTableOk] = useState(null); // null = checking, true/false = server verdict

  useEffect(() => {
    if (!table) { setTableOk(true); return; }
    api.get(`/table-valid/${encodeURIComponent(table)}`)
      .then((r) => setTableOk(!!r.data.valid))
      .catch(() => setTableOk(true)); // fail-open: a backend blip must never block real guests
  }, [table]);
  useEffect(() => { if (tableParam && !noTable && tableOk) localStorage.setItem("up_table", tableParam); }, [tableParam, noTable, tableOk]);
  useEffect(() => {
    api.get("/table-menu").then((r) => {
      setCatOrder(r.data.categories || []);
      setVarGroups(r.data.variation_groups || []);
      // photo order (owner fix round 2): POS dish image → fuzzy DoorDash match → beige initial tile
      setDishes((r.data.dishes || []).map((d) => ({
        ...d,
        img: d.image || DISH_PHOTOS[normName(d.name)] || null,
      })));
    }).catch(() => setDishes([]));
  }, []);

  const refreshMe = useCallback(() => {
    if (!phone) return Promise.resolve(null);
    return api.get("/loyalty/me", { params: { phone } }).then((r) => { setMe(r.data); return r.data; }).catch(() => null);
  }, [phone]);
  useEffect(() => { refreshMe(); }, [refreshMe]);

  const setPhone = (p) => { localStorage.setItem("up_phone", p); setPhoneState(p); };
  const setCart = (next) => { localStorage.setItem("up_cart", JSON.stringify(next)); setCartState(next); };
  const addToCart = (d) => setCart({ ...cart, [d.slug]: { slug: d.slug, name: d.name, price: d.price, image: d.img, qty: (cart[d.slug]?.qty || 0) + 1 } });
  // variation group ADD: display label "Parent: Option", identity/price = the real underlying dish
  const addVariant = (d, displayName) => setCart({ ...cart, [d.slug]: { slug: d.slug, name: displayName, price: d.price, image: d.img, qty: (cart[d.slug]?.qty || 0) + 1 } });
  const setQty = (slug, qty) => {
    const next = { ...cart };
    if (qty <= 0) delete next[slug]; else next[slug] = { ...next[slug], qty };
    setCart(next);
  };
  const itemCount = useMemo(() => Object.values(cart).reduce((n, i) => n + i.qty, 0), [cart]);
  const go = useCallback((s) => {
    if (noTable) return navigate(s === "menu" || s === "landing" ? "/" : `/go/${s}`);
    navigate(s === "landing" ? `/t/${table || ""}` : `/t/${table || "-"}/${s}`);
  }, [navigate, table, noTable]);

  const ctx = { table, phone, setPhone, me, setMe, refreshMe, dishes, catOrder, varGroups, cart, setCart, addToCart, addVariant, setQty, itemCount, go, userId: USER_ID, api };
  const Screen = SCREENS[screen];
  if (table && tableOk !== true) {
    return (
      <div className="up-root" data-testid="table-flow-root">
        <div className="up-screen ut">
          {tableOk === false ? <TableNotFound table={table} /> : null}
        </div>
      </div>
    );
  }
  return (
    <div className="up-root" data-testid="table-flow-root">
      <div className={`up-screen${["menu", "cart", "pay", "thanks"].includes(screen) ? " ut" : ""}`}>
        <Screen {...ctx} />
      </div>
    </div>
  );
}
